import {
  CpgRetailer,
  MAX_RETAILER_COUNT,
} from '@features/campaigns/subdomains/cpg';
import { contentSlideHeader } from '@features/lci-report/utils';
import { buildStyledTable } from '@features/lci-report/utils/build-table';
import { Slide, TextProps } from '@features/powerpoint';
import { formatters } from '@utils/formattersV2';
import { formatters as cpgFormatters } from '@features/campaigns/subdomains/cpg';
import { sanitizeOptimization } from '@utils/valueTransformers';

const TABLE_WIDTH = 11.56;
const RANKING_COLUMN_WIDTH = 0.56;

type MinimalCpgRetailer = Pick<
  CpgRetailer,
  | 'name'
  | 'projectedIncrementalSales'
  | 'incrementalSalesLift'
  | 'totalProjectedSales'
  | 'averagePricePerTransaction'
  | 'penetration'
  | 'averagePurchaseFrequency'
  | 'salesRate'
  | 'averageTotalBasketAmount'
  | 'salesLiftIndex'
> & {
  ranking?: number;
};

type TableField = {
  columnLabel: string;
  getValue: (retailer: MinimalCpgRetailer) => string;
  key: keyof MinimalCpgRetailer;
};

const tableFields = [
  {
    columnLabel: '',
    getValue: retailer => retailer.ranking?.toString() ?? '',
    key: 'ranking',
  },
  {
    columnLabel: 'Retailer',
    getValue: retailer => retailer.name,
    key: 'name',
  },
  {
    columnLabel: 'Projected Incr. Sales',
    getValue: retailer =>
      formatters.currency(retailer.projectedIncrementalSales, {
        full: true,
        decimals: 0,
      }),
    key: 'projectedIncrementalSales',
  },
  {
    columnLabel: 'Incremental Sales Lift',
    getValue: retailer =>
      formatters.lift(retailer.incrementalSalesLift, { decimals: 1 }),
    key: 'incrementalSalesLift',
  },
  {
    columnLabel: 'Total Projected Sales',
    getValue: retailer =>
      formatters.currency(retailer.totalProjectedSales, {
        full: true,
        decimals: 0,
      }),
    key: 'totalProjectedSales',
  },
  {
    columnLabel: 'Avg. Price per Transaction',
    getValue: retailer =>
      formatters.currency(retailer.averagePricePerTransaction),
    key: 'averagePricePerTransaction',
  },
  {
    columnLabel: 'Penetration',
    getValue: retailer =>
      formatters.percentage(retailer.penetration, { decimals: 2 }),
    key: 'penetration',
  },
  {
    columnLabel: 'Avg. Purchase Frequency',
    getValue: retailer =>
      cpgFormatters.formatAveragePurchaseFrequency(
        retailer.averagePurchaseFrequency
      ),
    key: 'averagePurchaseFrequency',
  },
  {
    columnLabel: 'Sales Rate',
    getValue: retailer => formatters.currency(retailer.salesRate),
    key: 'salesRate',
  },
  {
    columnLabel: 'Avg. Total Basket Amount',
    getValue: retailer =>
      formatters.currency(retailer.averageTotalBasketAmount),
    key: 'averageTotalBasketAmount',
  },
  {
    columnLabel: 'Optimization Index',
    getValue: retailer =>
      formatters.number(sanitizeOptimization(retailer.salesLiftIndex), {
        decimals: 2,
      }),
    key: 'salesLiftIndex',
  },
] as const satisfies readonly TableField[];

export type PerformanceByTopRetailersSlideProps = {
  slideMaster: string;
  recommendation: string;
  retailers: MinimalCpgRetailer[];
  excludedColumns: TableField['key'][];
  page: number;
  totalPages: number;
};

export const performanceByTopRetailersSlide = (
  props: PerformanceByTopRetailersSlideProps
): Slide => {
  const filteredTableFields = tableFields.filter(
    field => !props.excludedColumns.includes(field.key)
  );

  const columnsExcludingRanking = filteredTableFields.length - 1;
  const standardColumnSize =
    (TABLE_WIDTH - RANKING_COLUMN_WIDTH) / columnsExcludingRanking;

  return {
    slideMaster: props.slideMaster,
    texts: [
      contentSlideHeader(
        `Performance by Top ${MAX_RETAILER_COUNT} Retailers (${props.page}/${props.totalPages})`
      ),
      ...(props.recommendation.length > 0
        ? ([
            {
              text: props.recommendation,
              options: {
                fontSize: 10,
                h: 0.27,
                w: 11.56,
                x: 0.71,
                y: 6.61,
                fit: 'resize',
              },
            },
          ] as const satisfies TextProps[])
        : []),
    ],
    tables: [
      buildStyledTable({
        columns: filteredTableFields.map(field => field.columnLabel),
        rows: props.retailers.map(retailer =>
          filteredTableFields.map(field => field.getValue(retailer))
        ),
        options: {
          h: 4.89,
          w: TABLE_WIDTH,
          x: 0.76,
          y: 1.61,
          rowH: 0.38,
          colW: [
            RANKING_COLUMN_WIDTH,
            ...Array.from(
              { length: columnsExcludingRanking },
              () => standardColumnSize
            ),
          ],
        },
      }),
    ],
  };
};
